import { uniqPrimativeMerge } from '../../utils/arrayUtils';
import { FinancialType } from '../lookups';
import { DealerActionKeys as keys } from './dealerActionKeys';
import { DealerActionTypes } from './dealerActionTypes';
import {
  IDealer,
  IDealerFinancialSummary,
  IDealerMin,
  IPowerAssessment,
  IPreseasonApiResponse
} from './dealerInterfaces';

export interface IDealerState {
  accountNums: string[];
  dealerList: IDealerMin[];
  dealerListLoading: boolean;
  dealerPowerAssessments: { [index: string]: number[] };
  financials: { [index: string]: IDealerFinancialSummary[] };
  fullListLoaded: boolean;
  hash: { [index: string]: IDealer };
  loading: IDealerLoadingObj;
  openOrders: Record<string, { loadedAt: Date; amount: number }>;
  orderLists: { [index: string]: string[] };
  powerAssessments: { [index: number]: IPowerAssessment };
  preseason: IPreseasonObj;
  shipmentIds: { [index: string]: number[] };
  totalItems: {
    orders: { [index: string]: number };
    shipments: { [index: string]: number };
  };
}

interface IPreseasonObj {
  [index: string]: {
    [index: string]: {
      availableProgramGroups: { [index: string]: number[] };
      estimate: number | undefined;
      revised: number | undefined;
      selectedPrograms: { [index: string]: number };
    };
  };
}

export type DealerLoadingTypes = 'dealer' | 'orderList' | 'shipment' | 'financials' | 'preseason' | 'openOrders';

interface IDealerLoadingObj {
  [index: string]: { [index: string]: boolean };
}

const initialState: IDealerState = {
  accountNums: [],
  dealerList: [],
  dealerListLoading: false,
  dealerPowerAssessments: {},
  financials: {},
  fullListLoaded: false,
  hash: {},
  loading: {},
  openOrders: {},
  orderLists: {},
  powerAssessments: {},
  preseason: {},
  shipmentIds: {},
  totalItems: { orders: {}, shipments: {} }
};

export function dealerReducer(state: IDealerState = initialState, action: DealerActionTypes): IDealerState {
  switch (action.type) {
    case keys.ADD_DEALER_SHIPMENT_IDS:
      return {
        ...state,
        shipmentIds: {
          ...state.shipmentIds,
          [action.payload.accountNum]: uniqPrimativeMerge(
            state.shipmentIds[action.payload.accountNum],
            action.payload.shipmentIds
          )
        }
      };
    case keys.FETCH_DEALER:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload]: {
            ...state.loading[action.payload],
            dealer: true
          }
        }
      };
    case keys.FETCH_DEALER_FULFILLED:
      return {
        ...state,
        accountNums: uniqPrimativeMerge(state.accountNums, action.payload.accountNum),
        hash: {
          ...state.hash,
          [action.payload.accountNum]: action.payload
        },
        loading: {
          ...state.loading,
          [action.payload.accountNum]: {
            ...state.loading[action.payload.accountNum],
            dealer: false
          }
        }
      };
    case keys.FETCH_DEALER_REJECTED:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload]: {
            ...state.loading[action.payload],
            dealer: false
          }
        }
      };
    case keys.FETCH_DEALER_LIST_FULFILLED:
      return {
        ...state,
        dealerList: action.payload.dealers,
        fullListLoaded: Boolean(action.payload.full)
      };
    case keys.FETCH_DEALER_LIST_REJECTED:
      return {
        ...state,
        dealerListLoading: false
      };
    case keys.FETCH_DEALER_ORDERS:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.accountNum]: {
            ...state.loading[action.payload.accountNum],
            orderList: true
          }
        }
      };
    case keys.FETCH_DEALER_ORDERS_FULFILLED:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.accountNum]: {
            ...state.loading[action.payload.accountNum],
            orderList: false
          }
        },
        orderLists: {
          ...state.orderLists,
          [action.payload.accountNum]: uniqPrimativeMerge(
            state.orderLists[action.payload.accountNum],
            action.payload.response.items.map(x => x.orderNumber)
          )
        },
        totalItems: {
          ...state.totalItems,
          orders: {
            ...state.totalItems.orders,
            [action.payload.accountNum]: action.payload.response.totalItems
          }
        }
      };
    case keys.FETCH_DEALER_ORDERS_REJECTED:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload]: {
            ...state.loading[action.payload],
            orderList: false
          }
        }
      };
    case keys.SET_DEALER_FINANCIALS:
      return {
        ...state,
        financials: {
          ...state.financials,
          ...action.payload.reduce<{ [index: string]: IDealerFinancialSummary[] }>((hash, f) => {
            hash[f.accountNum] = uniqPrimativeMerge(hash[f.accountNum], f);
            return hash;
          }, {})
        }
      };
    case keys.SET_DEALER_OPEN_ORDERS:
      return {
        ...state,
        openOrders: {
          ...state.openOrders,
          [action.payload.accountNum]: {
            amount: action.payload.amount,
            loadedAt: new Date()
          }
        }
      };
    case keys.SET_DEALER_POWER_ASSESSMENTS:
      return {
        ...state,
        dealerPowerAssessments: {
          ...state.dealerPowerAssessments,
          ...action.payload.reduce<{ [index: string]: number[] }>(
            (hash, pa) => {
              hash[pa.accountNum] = uniqPrimativeMerge(hash[pa.accountNum], pa.powerAssessmentId);
              return hash;
            },
            { ...state.dealerPowerAssessments }
          )
        },
        powerAssessments: {
          ...state.powerAssessments,
          ...action.payload.reduce<{ [index: number]: IPowerAssessment }>((hash, pa) => {
            hash[pa.powerAssessmentId] = pa;
            return hash;
          }, {})
        }
      };
    case keys.SET_DEALER_PRESEASON:
      return {
        ...state,
        preseason: {
          ...state.preseason,
          ...processPreseasonResponse(state.preseason, action.payload)
        }
      };
    case keys.SET_DEALER_SHIPMENTS:
      return {
        ...state,
        shipmentIds: {
          ...state.shipmentIds,
          [action.payload.accountNum]: uniqPrimativeMerge(
            state.shipmentIds[action.payload.accountNum],
            action.payload.response.items.map(x => x.shipmentId)
          )
        },
        totalItems: {
          ...state.totalItems,
          shipments: {
            ...state.totalItems.shipments,
            [action.payload.accountNum]: action.payload.response.totalItems
          }
        }
      };
    case keys.SET_PRESEASON_GOAL:
      return {
        ...state,
        preseason: {
          ...state.preseason,
          [action.payload.accountNum]: {
            ...state.preseason[action.payload.accountNum],
            [action.payload.fiscalYear]: {
              ...state.preseason[action.payload.accountNum][action.payload.fiscalYear],
              estimate:
                action.payload.financialType === FinancialType.PreseasonEstimate
                  ? action.payload.amount
                  : state.preseason[action.payload.accountNum][action.payload.fiscalYear].estimate,
              revised:
                action.payload.financialType === FinancialType.PreseasonRevised
                  ? action.payload.amount
                  : state.preseason[action.payload.accountNum][action.payload.fiscalYear].revised
            }
          }
        }
      };
    case keys.SET_PRESEASON_PROGRAM:
      return {
        ...state,
        preseason: {
          ...state.preseason,
          [action.payload.accountNum]: {
            ...state.preseason[action.payload.accountNum],
            [action.payload.fiscalYear]: {
              ...state.preseason[action.payload.accountNum][action.payload.fiscalYear],
              selectedPrograms: {
                ...state.preseason[action.payload.accountNum][action.payload.fiscalYear].selectedPrograms,
                [action.payload.brand]: action.payload.programTargetLevelId
              }
            }
          }
        }
      };
    case keys.TOGGLE_DEALER_LIST_LOADING:
      return {
        ...state,
        dealerListLoading: !state.dealerListLoading
      };
    case keys.TOGGLE_DEALER_LOADING:
      const { accountNum, type } = action.payload;
      return {
        ...state,
        loading: {
          ...state.loading,
          [accountNum]: {
            ...state.loading[accountNum],
            [type]: state.loading[accountNum][type] ? !state.loading[accountNum][type] : true
          }
        }
      };
    default:
      return state;
  }
}

function processPreseasonResponse(original: IPreseasonObj, response: IPreseasonApiResponse): IPreseasonObj {
  const { accountNum, availableProgramGroups, estimateAmount, fiscalYear, revisedAmount, selectedPrograms } = response;

  let newObj: IPreseasonObj = { ...original };
  const exists: boolean = Boolean(original[accountNum]) && Boolean(original[accountNum][fiscalYear]);
  let newAvailableProgramGroups = exists ? original[accountNum][fiscalYear].availableProgramGroups : {};

  if (availableProgramGroups) {
    newAvailableProgramGroups = availableProgramGroups.reduce<{ [index: string]: number[] }>((hash, g) => {
      hash[g.brand] = g.programGroupIds;
      return hash;
    }, newAvailableProgramGroups);
  }

  newObj = {
    ...original,
    [accountNum]: {
      ...original[accountNum],
      [fiscalYear]: {
        availableProgramGroups: newAvailableProgramGroups,
        estimate: estimateAmount ? estimateAmount : exists ? original[accountNum][fiscalYear].estimate : undefined,
        revised: revisedAmount ? revisedAmount : exists ? original[accountNum][fiscalYear].revised : undefined,
        selectedPrograms: selectedPrograms.reduce<{ [index: string]: number }>(
          (hash, g) => {
            hash[g.brand] = g.programLevelId;
            return hash;
          },
          exists ? original[accountNum][fiscalYear].selectedPrograms : {}
        )
      }
    }
  };

  return newObj;
}
